/**
 * MapContainer — unified adapter that delegates to one of three map backends:
 *   1. DeckGL: deck.gl + MapLibre flat map (default, desktop)
 *   2. Globe3D: globe.gl 3D globe
 *   3. SVG: D3/SVG fallback (mobile / low-memory)
 *
 * Maintains cached data slots that survive mode switches.
 * When switching modes, rehydrateActiveMap() replays all cached data.
 */

import type { Layer } from '@deck.gl/core';
import type maplibregl from 'maplibre-gl';
import type { GlobeInstance } from 'globe.gl';
import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { initMapEngine, destroyMap } from './engine';
import { initGlobe, setDeckLayers, destroyGlobe } from './globe';
import { initGlobe3D, destroyGlobe3D, setGlobeTexture } from './globe-3d';
import { applyEnhancedVisuals, removeEnhancedVisuals } from './enhanced-visuals';
import { configureGlobeArcs, configureGlobePaths, type GlobeArc, type GlobePath } from './globe-arcs';
import { buildMarkerElement, getMarkerAltitude, type GlobeMarker } from './globe-markers';
import { initGlobeBridge, destroyGlobeBridge, type BridgeMarker } from './globe-bridge';
import {
  mergePolygonData,
  configureMergedPolygonAccessors,
  type ConflictZonePolygon,
  type CIIPolygon,
} from './globe-polygons';
import { getVisualPreset, getTextureUrl } from './globe-render-settings';
import { mapZoomToGlobeAltitude, globeAltitudeToMapZoom } from './globe-zoom';
import { initSVGMap, type SVGMapInstance, type SVGMarkerData } from './map-fallback-svg';
import { initLayerSystem, destroyLayerSystem } from './layers/index';
import { initMapInteractions, destroyMapInteractions } from './interactions';
import { initTooltip, destroyTooltip } from './tooltip';

// ── Types ────────────────────────────────────────────────────────────

export type MapMode = 'deckgl' | 'globe3d' | 'svg';

export interface MapContainerOptions {
  container: HTMLElement;
  preferGlobe?: boolean;
  initialMode?: MapMode;
}

interface CachedData {
  deckLayers: Layer[];
  globeMarkers: unknown[];
  globeArcs: unknown[];
  globePaths: unknown[];
  globePolygons: unknown[];
  globeLabels: unknown[];
  globeHtmlElements: unknown[];
  earthquakes: unknown[];
  militaryFlights: unknown[];
  vessels: unknown[];
  satellites: unknown[];
  fires: unknown[];
  conflicts: unknown[];
  hotspots: unknown[];
  nuclearFacilities: unknown[];
  militaryBases: unknown[];
  datacenters: unknown[];
  weatherAlerts: unknown[];
  radiationReadings: unknown[];
  gpsJammingHexes: unknown[];
  tradeRoutes: unknown[];
  cables: unknown[];
  pipelines: unknown[];
  orbitalPaths: unknown[];
  conflictZones: unknown[];
  ciiScores: unknown[];
  protests: unknown[];
  cyberEvents: unknown[];
  darkShips: unknown[];
  displacements: unknown[];
  naturalHazards: unknown[];
  ports: unknown[];
  newsMarkers: unknown[];
  predictionMarkets: unknown[];
  customPoints: unknown[];
  customArcs: unknown[];
  customPolygons: unknown[];
  svgMarkers: unknown[];
  [key: string]: unknown;
}

// ── State ────────────────────────────────────────────────────────────

let instance: MapContainer | null = null;

// ── MapContainer ─────────────────────────────────────────────────────

export class MapContainer {
  private _mode: MapMode;
  private _container: HTMLElement;
  private _cache: CachedData;
  private _initialized = false;
  private _switching = false;
  private _flatMap: maplibregl.Map | null = null;
  private _globe3d: GlobeInstance | null = null;
  private _svgMap: SVGMapInstance | null = null;

  constructor(options: MapContainerOptions) {
    this._container = options.container;
    this._mode = options.initialMode ?? (options.preferGlobe ? 'globe3d' : 'deckgl');
    this._cache = createEmptyCache();
    instance = this;
  }

  /** Initialize the active map backend. */
  async init(): Promise<void> {
    if (this._initialized) return;
    this._initialized = true;

    // Mark switching for the whole boot init so switchMode() (and the
    // toggle's guard) cannot interleave with _initDeckGL's await gaps —
    // a switch mid-init would stack two live backends or strand the
    // 'load' promise and leave the boot loader up forever.
    this._switching = true;
    try {
      if (this._mode === 'deckgl') {
        await this._initDeckGL();
      } else if (this._mode === 'globe3d') {
        await this._initGlobe3D();
      } else {
        await this._initSVG();
      }
    } finally {
      this._switching = false;
    }

    this._syncViewMode();
    window.dispatchEvent(new CustomEvent('map-mode-changed', { detail: { mode: this._mode } }));
  }

  /** Current rendering mode. */
  get mode(): MapMode {
    return this._mode;
  }

  /** Whether a mode switch is currently in progress. */
  get switching(): boolean {
    return this._switching;
  }

  /** Switch between map modes. Preserves all cached data. */
  async switchMode(mode: MapMode): Promise<void> {
    if (mode === this._mode || this._switching) return;
    this._switching = true;

    try {
      // Capture the outgoing camera into the store BEFORE teardown so the
      // incoming backend starts at the same view (engine and globe both
      // read the store viewport at init).
      this._captureCamera();

      // Tear down current
      this._teardownCurrent();

      this._mode = mode;

      // Initialize new
      if (mode === 'deckgl') {
        await this._initDeckGL();
      } else if (mode === 'globe3d') {
        await this._initGlobe3D();
      } else {
        await this._initSVG();
      }

      // Replay cached data
      this._rehydrate();
      this._syncViewMode();
      window.dispatchEvent(new CustomEvent('map-mode-changed', { detail: { mode } }));
    } finally {
      this._switching = false;
    }
  }

  // ── Data Setters (delegate to active backend) ──────────────────────

  /** Set deck.gl layers (flat map mode). */
  setDeckLayers(layers: Layer[]): void {
    this._cache.deckLayers = layers;
    if (this._mode === 'deckgl') {
      setDeckLayers(layers);
    }
  }

  /** Set globe HTML markers (globe mode). Accessors are configured at init. */
  setGlobeMarkers(markers: object[]): void {
    this._cache.globeMarkers = markers;
    if (this._mode === 'globe3d' && this._globe3d) {
      this._globe3d.htmlElementsData(markers);
    }
  }

  /** Set globe arcs with full arc configuration (globe mode). */
  setGlobeArcs(arcs: GlobeArc[]): void {
    this._cache.globeArcs = arcs;
    if (this._mode === 'globe3d' && this._globe3d) {
      configureGlobeArcs(this._globe3d, arcs);
    }
  }

  /** Set globe paths with full point/color accessors (globe mode). */
  setGlobePaths(paths: GlobePath[]): void {
    this._cache.globePaths = paths;
    if (this._mode === 'globe3d' && this._globe3d) {
      configureGlobePaths(this._globe3d, paths);
    }
  }

  /** Set conflict zone polygons on globe. */
  setConflictZones(zones: ConflictZonePolygon[]): void {
    this._cache.conflictZones = zones;
    if (this._mode === 'globe3d' && this._globe3d) {
      const cii = this._cache.ciiScores as CIIPolygon[];
      const merged = mergePolygonData(zones, cii);
      this._globe3d.polygonsData(merged);
    }
  }

  /** Set CII choropleth data on globe. */
  setCIIScores(countries: CIIPolygon[]): void {
    this._cache.ciiScores = countries;
    if (this._mode === 'globe3d' && this._globe3d) {
      const conflicts = this._cache.conflictZones as ConflictZonePolygon[];
      const merged = mergePolygonData(conflicts, countries);
      this._globe3d.polygonsData(merged);
    }
  }

  /** Set SVG markers (SVG fallback mode). */
  setSVGMarkers(markers: SVGMarkerData[]): void {
    this._cache.svgMarkers = markers;
    if (this._mode === 'svg' && this._svgMap) {
      this._svgMap.setMarkers(markers);
    }
  }

  /** Set globe polygons (globe mode). */
  setGlobePolygons(polygons: object[]): void {
    this._cache.globePolygons = polygons;
    if (this._mode === 'globe3d' && this._globe3d) {
      this._globe3d.polygonsData(polygons);
    }
  }

  /** Generic data slot setter — stores in cache and forwards to active backend. */
  setData(key: keyof CachedData, data: unknown[]): void {
    this._cache[key] = data;
  }

  /** Get the underlying MapLibre map (flat mode only). */
  getMap(): maplibregl.Map | null {
    return this._flatMap;
  }

  /** Get the underlying globe.gl instance (globe mode only). */
  getGlobe(): GlobeInstance | null {
    return this._globe3d;
  }

  /** Destroy the map container and all backends. */
  destroy(): void {
    this._teardownCurrent();
    this._cache = createEmptyCache();
    this._initialized = false;
    if (instance === this) instance = null;
  }

  // ── Private: Camera / viewport sync ────────────────────────────────

  /** Write the active backend's camera into the store viewport. */
  private _captureCamera(): void {
    if (this._mode === 'deckgl' && this._flatMap) {
      const center = this._flatMap.getCenter();
      appStore.setState({
        viewport: {
          ...appStore.getState().viewport,
          center: [center.lng, center.lat],
          zoom: this._flatMap.getZoom(),
          bearing: 0,
          pitch: 0,
        },
      });
    } else if (this._mode === 'globe3d' && this._globe3d) {
      const pov = this._globe3d.pointOfView();
      if (pov && Number.isFinite(pov.lat) && Number.isFinite(pov.lng)) {
        appStore.setState({
          viewport: {
            ...appStore.getState().viewport,
            center: [pov.lng, pov.lat],
            zoom: globeAltitudeToMapZoom(pov.altitude),
            bearing: 0,
            pitch: 0,
          },
        });
      }
    }
  }

  /** Keep the store's viewMode discriminator in sync with the active backend. */
  private _syncViewMode(): void {
    const viewMode = this._mode === 'globe3d' ? 'globe' : 'flat';
    const state = appStore.getState();
    if (state.viewport.viewMode !== viewMode) {
      appStore.setState({ viewport: { ...state.viewport, viewMode } });
    }
  }

  // ── Private: Backend Initialization ────────────────────────────────

  private async _initDeckGL(): Promise<void> {
    this._flatMap = await initMapEngine({ container: this._container });

    await new Promise<void>((resolve) => {
      if (!this._flatMap) return resolve();
      if (this._flatMap.loaded() || this._flatMap.isStyleLoaded()) {
        resolve();
      } else {
        this._flatMap.once('load', () => resolve());
      }
    });

    if (this._flatMap) {
      initGlobe({ map: this._flatMap });
      initTooltip(this._container);
      initLayerSystem();
      initMapInteractions();
    }
  }

  private async _initGlobe3D(): Promise<void> {
    const { viewport } = appStore.getState();
    this._globe3d = initGlobe3D({
      container: this._container,
      textureUrl: getTextureUrl(),
      initialView: {
        lat: viewport.center[1],
        lng: viewport.center[0],
        altitude: mapZoomToGlobeAltitude(viewport.zoom),
      },
    });

    // Apply cosmos preset if active
    if (getVisualPreset() === 'enhanced' && this._globe3d) {
      applyEnhancedVisuals(this._globe3d);
    }

    // Configure marker + polygon accessors once — data setters push only.
    if (this._globe3d) {
      this._globe3d
        .htmlLat((d: object) => (d as { lat: number }).lat)
        .htmlLng((d: object) => (d as { lng: number }).lng)
        .htmlAltitude((d: object) => getMarkerAltitude(d as GlobeMarker))
        .htmlElement((d: object) => {
          const el = buildMarkerElement(d as GlobeMarker);
          const sourceEvent = (d as BridgeMarker)._event;
          if (sourceEvent) {
            el.addEventListener('click', (evt) => {
              evt.stopPropagation();
              // Synthetic static events are stamped at click time like the
              // flat map's staticToEvent; real events keep their own time.
              eventBus.emit('event:selected', sourceEvent.source === 'static'
                ? { ...sourceEvent, timestamp: new Date().toISOString() }
                : sourceEvent);
            });
          }
          return el;
        });
      configureMergedPolygonAccessors(this._globe3d);
    }

    // Listen for settings changes
    window.addEventListener('globe-settings-changed', this._onGlobeSettingsChanged);

    // Start the data bridge — populates markers/polygons from app state
    initGlobeBridge(this);
  }

  private _onGlobeSettingsChanged = (e: Event): void => {
    const detail = (e as CustomEvent).detail;
    if (!this._globe3d || !detail?.key) return;
    // detail.key carries the full storage key (see globe-render-settings notify)
    if (String(detail.key).endsWith('preset')) {
      if (detail.value === 'enhanced') {
        applyEnhancedVisuals(this._globe3d);
      } else {
        removeEnhancedVisuals(this._globe3d);
      }
    } else if (String(detail.key).endsWith('texture')) {
      setGlobeTexture(getTextureUrl());
    }
  };

  private async _initSVG(): Promise<void> {
    this._svgMap = initSVGMap({ container: this._container });
  }

  // ── Private: Teardown ──────────────────────────────────────────────

  private _teardownCurrent(): void {
    if (this._mode === 'deckgl') {
      destroyMapInteractions();
      destroyLayerSystem();
      destroyTooltip();
      destroyGlobe();
      destroyMap();
      this._flatMap = null;
    } else if (this._mode === 'globe3d') {
      destroyGlobeBridge();
      window.removeEventListener('globe-settings-changed', this._onGlobeSettingsChanged);
      if (this._globe3d) {
        removeEnhancedVisuals(this._globe3d);
      }
      destroyGlobe3D();
      this._globe3d = null;
    } else if (this._mode === 'svg') {
      this._svgMap?.destroy();
      this._svgMap = null;
    }
    this._container.innerHTML = '';
  }

  // ── Private: Rehydrate ─────────────────────────────────────────────

  private _rehydrate(): void {
    if (this._mode === 'deckgl' && this._cache.deckLayers.length > 0) {
      setDeckLayers(this._cache.deckLayers);
    }

    if (this._mode === 'globe3d' && this._globe3d) {
      if (this._cache.globeMarkers.length > 0) this.setGlobeMarkers(this._cache.globeMarkers as object[]);
      if (this._cache.globeArcs.length > 0) this.setGlobeArcs(this._cache.globeArcs as GlobeArc[]);
      if (this._cache.globePaths.length > 0) this.setGlobePaths(this._cache.globePaths as GlobePath[]);
      if (this._cache.globePolygons.length > 0) this.setGlobePolygons(this._cache.globePolygons as object[]);
      if ((this._cache.conflictZones as unknown[]).length > 0) {
        this.setConflictZones(this._cache.conflictZones as ConflictZonePolygon[]);
      }
      if ((this._cache.ciiScores as unknown[]).length > 0) {
        this.setCIIScores(this._cache.ciiScores as CIIPolygon[]);
      }
    }

    if (this._mode === 'svg' && this._svgMap) {
      if (this._cache.svgMarkers.length > 0) this.setSVGMarkers(this._cache.svgMarkers as SVGMarkerData[]);
    }
  }
}

// ── Module API ───────────────────────────────────────────────────────

export function getMapContainer(): MapContainer | null {
  return instance;
}

// ── Helpers ──────────────────────────────────────────────────────────

function createEmptyCache(): CachedData {
  return {
    deckLayers: [],
    globeMarkers: [],
    globeArcs: [],
    globePaths: [],
    globePolygons: [],
    globeLabels: [],
    globeHtmlElements: [],
    earthquakes: [],
    militaryFlights: [],
    vessels: [],
    satellites: [],
    fires: [],
    conflicts: [],
    hotspots: [],
    nuclearFacilities: [],
    militaryBases: [],
    datacenters: [],
    weatherAlerts: [],
    radiationReadings: [],
    gpsJammingHexes: [],
    tradeRoutes: [],
    cables: [],
    pipelines: [],
    orbitalPaths: [],
    conflictZones: [],
    ciiScores: [],
    protests: [],
    cyberEvents: [],
    darkShips: [],
    displacements: [],
    naturalHazards: [],
    ports: [],
    newsMarkers: [],
    predictionMarkets: [],
    customPoints: [],
    customArcs: [],
    customPolygons: [],
    svgMarkers: [],
  };
}
